import { SchedulingSettings } from "@/data/models/SchedulingSettings";
import {
  Task,
  ScheduledTask,
  isScheduledTask,
  getMinStartDateTimeForTaskType,
} from "@/data/models/Task";
import { DateTime, Interval } from "luxon";
import { UserLookup } from "@/data/models/UserLookup";
import {
  getQualificationsOfUser,
  hasUserRequieredQualfications,
  Qualification,
} from "@/data/models/Qualification";
import User from "@/data/models/User";

import { TimeSlotsIterator } from "./TimeSlotsIterator";
import { TasksOfUserLookup } from "./TasksOfUserLookup";
import { getDateFromDayId, getDayId } from "./TimeSlot";
import { SchedulingResult } from "@/data/models/SchedulingResult";
import { calculateObjectiveFunctionRelativeToFocusedTask } from "./SchedulingObjectiveFunction";

export class TaskScheduler {
  private schedulingSettings: SchedulingSettings;

  private sortedUsers: User[];
  private tasksOfUser: Map<string, TasksOfUserLookup>;
  private userQualificationLookup = new Map<string, Set<Qualification>>();
  private userLookup: UserLookup;

  constructor(
    tasks: Task[],
    sortedUsers: User[],
    userLookup: UserLookup,
    schedulingSettings: SchedulingSettings
  ) {
    this.schedulingSettings = schedulingSettings;
    this.tasksOfUser = new Map();
    this.userLookup = userLookup;

    const scheduledTasksByUser = new Map<string, ScheduledTask[]>();

    tasks.forEach((task) => {
      if (isScheduledTask(task)) {
        if (!scheduledTasksByUser.has(task.userId)) {
          scheduledTasksByUser.set(task.userId, []);
        }

        scheduledTasksByUser.get(task.userId)?.push(task);
      }
    });

    sortedUsers.forEach((user) => {
      this.userQualificationLookup.set(
        user.id,
        new Set(getQualificationsOfUser(user))
      );

      const scheduledTasks = scheduledTasksByUser.get(user.id) ?? [];

      this.tasksOfUser.set(
        user.id,
        new TasksOfUserLookup(
          user,
          scheduledTasks,
          schedulingSettings.travelTimeSettings,
          schedulingSettings.planningControlSettings,
          schedulingSettings.workingHoursSettings
        )
      );
    });

    this.sortedUsers = sortedUsers;
  }

  getPossibleUsersForTask(task: Task) {
    const { targetUserId, isTargetUserFixed } = task.planningParameters;

    let usersToIterate = this.sortedUsers;

    let targetUser: User | undefined = undefined;
    if (targetUserId) {
      targetUser = this.userLookup.getUser(targetUserId);

      if (isTargetUserFixed) {
        if (targetUser) {
          usersToIterate = [targetUser];
        } else {
          usersToIterate = [];
        }
      } else {
        if (targetUser) {
          //Move user to front so he will be prioritized
          usersToIterate = [
            targetUser,
            ...usersToIterate.filter((other) => other.id !== targetUser?.id),
          ];
        }
      }
    }

    const { requiredQualification } = task;

    usersToIterate = usersToIterate.filter((user) =>
      hasUserRequieredQualfications(
        this.userQualificationLookup.get(user.id),
        requiredQualification
      )
    );

    return usersToIterate;
  }

  private scheduleTask(
    startFrom: Date,
    task: Task,
    skipWeekends: boolean,
    options?: {
      forceStartFrom?: boolean;
      forcedUserId?: string;
    }
  ): SchedulingResult | null {
    const { forceStartFrom, forcedUserId } = options ?? {};

    //If the task alone will take longer than the allowed max working hours per day it cannot be scheduled
    const {
      defaultTravelTimeBetweenTaskAndDepotInMin,
    } = this.schedulingSettings.travelTimeSettings;

    const minTimeToCompleteTask =
      task.durationInMin + 2 * defaultTravelTimeBetweenTaskAndDepotInMin;

    const maxWorkingHoursPerDayInMin =
      this.schedulingSettings.workingHoursSettings.maxWorkingHoursPerDay * 60;

    if (minTimeToCompleteTask > maxWorkingHoursPerDayInMin) {
      return null;
    }

    const dateTime = DateTime.fromJSDate(startFrom);

    const startDate = forceStartFrom
      ? startFrom
      : getMinStartDateTimeForTaskType(
          dateTime,
          task.taskType,
          !skipWeekends
        ).toJSDate();

    let usersToIterate: User[] = [];

    if (forcedUserId) {
      const forcedUser = this.userLookup.getUser(forcedUserId);

      if (forcedUser) {
        usersToIterate = [forcedUser];
      }
    } else {
      usersToIterate = this.getPossibleUsersForTask(task);
    }

    if (usersToIterate.length === 0) {
      return null;
    }

    const iterator = new TimeSlotsIterator(
      startDate,
      task,
      this.schedulingSettings,
      skipWeekends,
      forceStartFrom
    );

    let timeSlot = iterator.getNextTimeslot();

    const taskDurationInMs = task.durationInMin * 60000;

    let numTries = 0;

    const isTimeFixed = task.planningParameters.isTargetTimeFixed;

    while (timeSlot && numTries++ < 100) {
      for (const user of usersToIterate) {
        const tasksOfUser = this.tasksOfUser.get(user.id);

        if (tasksOfUser) {
          const dayId = getDayId(new Date(timeSlot.startTimestamp));
          const timeSlotsOnDay = tasksOfUser.getTimeSlotsToTry(
            dayId,
            taskDurationInMs
          );

          const timeSlotsToTry = isTimeFixed
            ? [timeSlot]
            : timeSlotsOnDay ?? [timeSlot];

          let bestResult: ScheduledTask | null = null;

          const freeTimeSlots = timeSlotsToTry.filter(
            (timeSlot) =>
              tasksOfUser.isTimeslotFree(timeSlot) &&
              (!forceStartFrom ||
                startDate.getTime() <= timeSlot.startTimestamp)
          );

          for (const freeTimeSlot of freeTimeSlots) {
            const scheduledTask: ScheduledTask = {
              ...task,
              startDate: new Date(freeTimeSlot.startTimestamp),
              endDate: new Date(
                freeTimeSlot.startTimestamp + freeTimeSlot.durationInMs
              ),
              wasConfirmed: false,
              userId: user.id,
            };

            const score = calculateObjectiveFunctionRelativeToFocusedTask(
              scheduledTask
            );

            scheduledTask.schedulingScore = score;

            if (
              score &&
              (!bestResult ||
                (bestResult.schedulingScore?.total ?? 0) < score.total)
            ) {
              bestResult = scheduledTask;
            }
          }

          if (bestResult) {
            return {
              scheduledTask: bestResult,
              schedulingScore: bestResult.schedulingScore ?? null,
            };
          }
        }
      }

      timeSlot = iterator.getNextTimeslot();
    }

    return null;
  }

  getIdsOfAllDaysInWeek(dayId: string, map: Map<string, string[]>): string[] {
    const dayIds = map.get(dayId);

    if (dayIds) {
      return dayIds;
    } else {
      const date = getDateFromDayId(dayId);

      const result = Interval.fromDateTimes(
        DateTime.fromJSDate(date).startOf("week"),
        DateTime.fromJSDate(date).endOf("week")
      )
        .splitBy({ days: 1 })
        .map((d) => getDayId(d.start.toJSDate()));

      map.set(dayId, result);
      result.forEach((id) => map.set(id, result));

      return result;
    }
  }

  scheduleTasks(
    tasks: Task[],
    skipWeekends: boolean,
    options?: {
      forcedStartDate?: Date;
      forcedUserId?: string;
    }
  ) {
    const { forcedStartDate, forcedUserId } = options ?? {};

    const startDate = options?.forcedStartDate ?? new Date();

    const sortedTasks = tasks.sort(
      (a, b) => b.earliestStartDate.getTime() - a.earliestStartDate.getTime()
    );

    const result: SchedulingResult[] = [];

    for (const task of sortedTasks) {
      const scheduledTask = this.scheduleTask(startDate, task, skipWeekends, {
        forceStartFrom: forcedStartDate !== undefined,
        forcedUserId,
      });

      if (scheduledTask) {
        result.push(scheduledTask);
      }
    }

    return result;
  }
}
